import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { carrierToLabel } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { MarkAsFulfilledWithDataInput } from "@redotech/redo-merchant-fulfillment-rpc/schema/mark-as-fulfilled-with-data";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import {
  dateToPlainDateTime,
  plainDateTimeToDate,
} from "@redotech/util/temporal";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { useContainsTiktokShippingOrder } from "../../utils/use-group-info";
import type { BinSelections } from "../bin-selection";
import {
  BinSelectionModal,
  InventoryAdjustmentWarningModal,
  useBinSelectionModal,
} from "../bin-selection-modal";
import { getWmsInventoryResolution } from "../wms-inventory-resolution";
import {
  applyBulkUpdateToRows,
  defaultBulkUpdate,
  fulfillmentGroupToRowInput,
  FulfillmentModalRowInput,
  markAsFulfilledForm,
  MarkAsFulfilledFormValue,
  ProvidersRequiringTrackingForFulfillment,
} from "./mark-as-fulfilled-form";
import { MarkAsFulfilledModalTable } from "./mark-as-fulfilled-modal-table";
import * as styles from "./mark-as-fulfilled-modal.module.css";

export const carrierOptions: RedoListItem<Carriers>[] = Object.values(
  Carriers,
).map((carrier) => ({
  id: carrier,
  value: carrier,
  type: "text",
  text: carrierToLabel[carrier],
}));

const notifyCustomerOptions: RedoListItem<boolean>[] = [
  { id: "notify-customer", value: true, type: "text", text: "Notify customer" },
  {
    id: "do-not-notify-customer",
    value: false,
    type: "text",
    text: "Do not notify customer",
  },
];

const notifyMarketplaceOptions: RedoListItem<boolean>[] = [
  {
    id: "notify-marketplace",
    value: true,
    type: "text",
    text: "Notify marketplace",
  },
  {
    id: "do-not-notify-marketplace",
    value: false,
    type: "text",
    text: "Do not notify marketplace",
  },
];

const formDataToInput = (
  formData: MarkAsFulfilledFormValue,
): MarkAsFulfilledWithDataInput => {
  return formData.rowData.map((row) => ({
    fulfillmentGroupId: row.fulfillmentGroupId,
    shipDate: row.shipDate,
    shippingSelection: row.shippingSelection,
    fulfillmentDate: formData.bulkUpdate.shipDate,
    trackingNumber: row.trackingNumber,
    notifyMarketplace: row.notifyMarketplace,
    notifyCustomer: row.notifyCustomer,
    transferInventory: row.transferInventory,
  }));
};

export const MarkAsFulfilledModal = memo(function MarkAsFulfilledModal({
  open,
  onClose,
  groups,
  onGroupsFulfillment,
}: {
  open: boolean;
  onClose: () => void;
  groups: readonly FulfillmentGroupData[];
  onGroupsFulfillment?: (groupIds: string[]) => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);

  const transferInventoryAvailable =
    !!team?.settings?.outboundLabels?.wms?.sourceOfTruthEnabled;

  const defaultFormValue: MarkAsFulfilledFormValue = {
    defaultValue: "",
    bulkUpdate: {
      ...defaultBulkUpdate,
      transferInventory: transferInventoryAvailable,
    },
    rowData: groups.map((group) =>
      fulfillmentGroupToRowInput(group, transferInventoryAvailable),
    ),
  };

  const form = useInput(markAsFulfilledForm, defaultFormValue);

  const containsTiktokShippingOrder = useContainsTiktokShippingOrder(groups);

  const formInitializedRef = useRef(false);
  useEffect(() => {
    if (groups.length > 0 && !formInitializedRef.current) {
      formInitializedRef.current = true;
      form.setValue({
        ...form.value,
        rowData: groups.map((group) =>
          fulfillmentGroupToRowInput(group, transferInventoryAvailable),
        ),
      });
    }
  }, [groups, form, transferInventoryAvailable]);

  const setBulkUpdate = useHandler(
    (
      key: keyof MarkAsFulfilledFormValue["bulkUpdate"],
      value: MarkAsFulfilledFormValue["bulkUpdate"][keyof MarkAsFulfilledFormValue["bulkUpdate"]],
    ) => {
      const newValue = {
        ...form.value,
        bulkUpdate: { ...form.value.bulkUpdate, [key]: value },
        rowData: applyBulkUpdateToRows(form.value.rowData, key, value),
      };

      // Don't let the user try to notify the customer without also notifying the marketplace
      if (key === "notifyMarketplace" && value === false) {
        newValue.bulkUpdate.notifyCustomer = false;
        newValue.rowData = newValue.rowData.map((row) => ({
          ...row,
          notifyCustomer: false,
        }));
      } else if (key === "notifyCustomer" && value === true) {
        newValue.rowData = newValue.rowData.map((row) => ({
          ...row,
          notifyCustomer: row.notifyMarketplace && value,
        }));
      }

      form.setValue(newValue);
    },
  );

  const rowUpdate = useHandler(
    (index: number, update: Partial<FulfillmentModalRowInput>) => {
      if (update.notifyMarketplace === false) {
        update.notifyCustomer = false;
      }

      if (
        (update.trackingNumber === "" || update.shippingSelection === null) &&
        ProvidersRequiringTrackingForFulfillment.some((provider) =>
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          form.value.rowData[index].providers?.includes(provider),
        )
      ) {
        update.notifyMarketplace = false;
        update.notifyCustomer = false;
      }

      form.setValue({
        ...form.value,
        rowData: form.value.rowData.map((row, i) =>
          i === index ? { ...row, ...update } : row,
        ),
      });
    },
  );

  const {
    modalProps: binSelectionModalProps,
    inventoryAdjustmentWarningModalProps,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
  } = useBinSelectionModal();

  const shouldCheckBins =
    open && transferInventoryAvailable && groups.length > 0;
  const [binsResolved, setBinsResolved] = useState(false);
  const [pendingBinPicks, setPendingBinPicks] = useState<{
    fulfillmentGroupIds: string[];
    binSelections: BinSelections;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setBinsResolved(false);
      setPendingBinPicks(null);
    }
  }, [open]);

  const availableBinsLoad = useLoad(
    async (signal) => {
      if (!shouldCheckBins) {
        return null;
      }
      const result = await client.getAvailableBinsForFulfillmentGroups(
        { fulfillmentGroupIds: groups.map((g) => g._id.toString()) },
        { signal },
      );
      return {
        resolution: getWmsInventoryResolution(result.lineItems),
        lineItems: result.lineItems,
        manualPickFulfillmentGroupIds: result.manualPickFulfillmentGroupIds,
      };
    },
    [shouldCheckBins, groups, client],
  );

  const skipInventoryDecrement = useHandler(() => {
    setBulkUpdate("transferInventory", false);
    setPendingBinPicks(null);
    setBinsResolved(true);
  });

  const promptedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      promptedRef.current = false;
      return;
    }
    if (!shouldCheckBins) {
      setBinsResolved(true);
      return;
    }
    if (promptedRef.current || availableBinsLoad.pending) {
      return;
    }
    if (availableBinsLoad.error) {
      promptedRef.current = true;
      console.error(
        "mark-as-fulfilled: failed to check bin availability",
        availableBinsLoad.error,
      );
      toast("Could not check bin availability. Please try again.", {
        variant: "error",
      });
      onClose();
      return;
    }
    const value = availableBinsLoad.value;
    if (!value) {
      return;
    }
    promptedRef.current = true;
    const { resolution, lineItems, manualPickFulfillmentGroupIds } = value;
    if (resolution === "inventory-adjustment-warning") {
      openInventoryAdjustmentWarningModal({
        lineItems,
        onConfirm: () => setBinsResolved(true),
        onCancel: () => onClose(),
        onBypass: skipInventoryDecrement,
        bypassLabel: "Skip inventory decrement",
        confirmLabel: "Continue to mark as fulfilled",
      });
    } else if (resolution === "bin-selection") {
      openBinSelectionModal({
        lineItems,
        fulfillmentGroupIds: manualPickFulfillmentGroupIds,
        recordOnConfirm: false,
        onConfirm: (binSelections) => {
          setPendingBinPicks({
            fulfillmentGroupIds: manualPickFulfillmentGroupIds,
            binSelections,
          });
          setBinsResolved(true);
        },
        onCancel: () => onClose(),
        onBypass: skipInventoryDecrement,
        bypassLabel: "Skip inventory decrement",
        confirmLabel: "Confirm and mark as fulfilled",
      });
    } else {
      setBinsResolved(true);
    }
  }, [
    open,
    shouldCheckBins,
    availableBinsLoad,
    openBinSelectionModal,
    openInventoryAdjustmentWarningModal,
    skipInventoryDecrement,
    onClose,
  ]);

  const finalize = useHandler(async (data: MarkAsFulfilledWithDataInput) => {
    try {
      if (onGroupsFulfillment) {
        onGroupsFulfillment(groups.map((g) => g._id));
      }
      await client.markAsFulfilledWithData(data);
      toast("Marked as fulfilled", {
        variant: "success",
        duration: Temporal.Duration.from({ seconds: 3 }),
      });
    } catch (error) {
      console.error(error);
      toast("Error marking as fulfilled", { variant: "error" });
    } finally {
      onClose();
    }
  });

  const [markFulfilledLoad, doMarkFulfilled] = useTriggerLoad(async () => {
    const data: MarkAsFulfilledWithDataInput = formDataToInput(form.value);

    if (data.some((d) => d.notifyMarketplace && containsTiktokShippingOrder)) {
      toast("Cannot mark TikTok Shipping orders as fulfilled", {
        variant: "error",
      });
      return;
    }

    if (pendingBinPicks) {
      const transferringGroupIds = new Set(
        data
          .filter((d) => d.transferInventory)
          .map((d) => d.fulfillmentGroupId),
      );
      const fulfillmentGroupIds = pendingBinPicks.fulfillmentGroupIds.filter(
        (id) => transferringGroupIds.has(id),
      );
      if (fulfillmentGroupIds.length > 0) {
        try {
          await client.recordDirectBinPicks({
            fulfillmentGroupIds,
            binSelections: pendingBinPicks.binSelections,
          });
        } catch (error) {
          console.error(error);
          toast("Failed to record bin selection", { variant: "error" });
          return;
        }
      }
    }

    await finalize(data);
  });

  const binPromptOpen =
    binSelectionModalProps.open || inventoryAdjustmentWarningModalProps.open;

  return (
    <>
      <BinSelectionModal {...binSelectionModalProps} />
      <InventoryAdjustmentWarningModal
        {...inventoryAdjustmentWarningModalProps}
      />
      <RedoBaseModal
        isOpen={open && !binPromptOpen}
        onModalCloseRequested={() => {
          onClose();
        }}
        otherClasses={[styles.shipmentModal]}
      >
        <RedoModalHeader
          cancelClicked={onClose}
          headerBorder
          title="Mark as fulfilled"
        />
        {!binsResolved ? (
          <Flex
            align="center"
            dir="column"
            gap="md"
            grow={1}
            justify="center"
            p="3xl"
            w="full"
          >
            <RedoSpinner size="lg" />
            <Text fontSize="sm" textColor="secondary">
              Checking bin availability
            </Text>
            <RedoButton
              hierarchy="secondary"
              onClick={onClose}
              size="md"
              text="Cancel"
              theme="normal"
            />
          </Flex>
        ) : (
          <Flex
            className={styles.shipmentModalContent}
            dir="column"
            gap="none"
            grow={1}
            w="full"
          >
            <Flex align="flex-start" dir="row" gap="md" pb="md" pl="2xl">
              <Text fontSize="sm" textColor="primary">
                Bulk update
              </Text>
            </Flex>
            <Flex dir="row" gap="none" pb="md" w="full">
              <Flex dir="row" gap="xl" pb="sm" px="2xl">
                <Flex
                  className={styles.shipDateInput}
                  dir="column"
                  gap="sm"
                  p="none"
                >
                  <RedoSingleDatePicker
                    buttonSize="sm"
                    date={dateToPlainDateTime(
                      form.value.bulkUpdate.shipDate,
                    ).toPlainDate()}
                    dateStringOptions={{ year: undefined }}
                    maxDate={dateToPlainDateTime(new Date()).toPlainDate()}
                    setDate={(plainDate) => {
                      setBulkUpdate(
                        "shipDate",
                        plainDateTimeToDate(plainDate.toPlainDateTime()),
                      );
                    }}
                  />
                </Flex>
                <RedoSingleSelectDropdownInput<Carriers>
                  className={styles.fulfillmentInput}
                  disabled={false}
                  options={carrierOptions}
                  optionSelected={({ item: carrier }) => {
                    setBulkUpdate("shippingSelection", carrier.value);
                  }}
                  placeholder="Select carrier"
                  selectedItem={carrierOptions.find(
                    (carrier) =>
                      carrier.value === form.value.bulkUpdate.shippingSelection,
                  )}
                  size="xs"
                />
                <DisabledTooltip
                  disabledMessage={
                    !form.value.bulkUpdate.notifyMarketplace
                      ? "Cannot notify customer without notifying marketplace"
                      : undefined
                  }
                >
                  <RedoSingleSelectDropdownInput<boolean>
                    className={styles.fulfillmentInput}
                    disabled={!form.value.bulkUpdate.notifyMarketplace}
                    options={notifyCustomerOptions}
                    optionSelected={({ item: notifyCustomer }) => {
                      setBulkUpdate("notifyCustomer", notifyCustomer.value);
                    }}
                    selectedItem={notifyCustomerOptions.find(
                      (notifyCustomer) =>
                        notifyCustomer.value ===
                        form.value.bulkUpdate.notifyCustomer,
                    )}
                    size="xs"
                  />
                </DisabledTooltip>
                <RedoSingleSelectDropdownInput<boolean>
                  className={styles.fulfillmentInput}
                  disabled={false}
                  options={notifyMarketplaceOptions}
                  optionSelected={({ item: notifyMarketplace }) => {
                    setBulkUpdate("notifyMarketplace", notifyMarketplace.value);
                  }}
                  selectedItem={notifyMarketplaceOptions.find(
                    (notifyMarketplace) =>
                      notifyMarketplace.value ===
                      form.value.bulkUpdate.notifyMarketplace,
                  )}
                  size="xs"
                />
                {transferInventoryAvailable && (
                  <RedoToggle
                    description="Decrement inventory levels in warehouse bins"
                    label="Decrement inventory"
                    setValue={(value) => {
                      setBulkUpdate("transferInventory", value);
                    }}
                    size="xs"
                    value={form.value.bulkUpdate.transferInventory}
                  />
                )}
              </Flex>
            </Flex>
            <Flex className={styles.tableContainer} w="full">
              <MarkAsFulfilledModalTable doUpdate={rowUpdate} form={form} />
            </Flex>
            <Flex
              align="center"
              borderColor="primary"
              borderStyle="solid"
              borderTopWidth="1px"
              className={styles.footer}
              dir="row"
              justify="flex-end"
              p="3xl"
              w="full"
            >
              <RedoButton
                hierarchy="secondary"
                onClick={onClose}
                size="md"
                text="Cancel"
                theme="normal"
              />
              <RedoButton
                disabled={markFulfilledLoad.pending}
                hierarchy="primary"
                onClick={() => {
                  doMarkFulfilled();
                }}
                pending={markFulfilledLoad.pending}
                size="md"
                text="Mark as fulfilled"
                theme="success"
                type="submit"
              />
            </Flex>
          </Flex>
        )}
      </RedoBaseModal>
    </>
  );
});
